import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import { Box, Chip, CircularProgress, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { SetAlertType } from "schemas/functions";

import { fetcherAuth } from "utils/api";

interface Props {
  organizationId: number;
  setAlert: SetAlertType;
}

interface PlanStep {
  id: number;
  priority: number;
  type: string;
  status: string;
  action: string;
  detail: string;
  targetTemplateId: number | null;
  targetTemplateName: string | null;
  targetBentoCategoryId: number | null;
  completedAt: string | null;
}

interface Plan {
  id: number;
  status: string;
  generationTrigger: string;
  createdAt: string | null;
  updatedAt: string | null;
  steps: PlanStep[];
}

export default function AdminPersonalizedPlan({
  organizationId,
  setAlert,
}: Props) {
  const { getToken } = useAuth();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [plan, setPlan] = useState<Plan | null>(null);

  const fetchPlan = async () => {
    setLoading(true);
    try {
      const { plan: fetched } = await fetcherAuth(
        getToken,
        `/api/admin/personalized-plans/${organizationId}`,
        "GET",
      );
      setPlan(fetched);
    } catch (error) {
      setAlert(
        error?.message ||
          "An error occurred while fetching the personalized plan",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const { plan: fetched } = await fetcherAuth(
        getToken,
        `/api/admin/personalized-plans/${organizationId}/generate`,
        "POST",
      );
      setPlan(fetched);
      setAlert("Personalized plan generated", "success");
    } catch (error) {
      setAlert(
        error?.message ||
          "An error occurred while generating the personalized plan",
        "error",
      );
    } finally {
      setGenerating(false);
    }
  };

  useEffect(() => {
    fetchPlan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId]);

  const formatDate = (iso: string | null) =>
    iso ? new Date(iso).toLocaleString() : "—";

  return (
    <Box>
      <Typography variant="h6">Personalized Plan</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        The active plan surfaced in the in-product assistant. Generating
        archives any prior active plan and creates a fresh one synchronously.
        Takes up to ~30s because it calls OpenAI.
      </Typography>

      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <LoadingButton
          loading={generating}
          onClick={handleGenerate}
          variant="outlined"
          size="small"
        >
          {plan ? "Regenerate Personalized Plan" : "Generate Personalized Plan"}
        </LoadingButton>
        {plan && (
          <Typography variant="caption" color="text.secondary">
            Last generated: {formatDate(plan.createdAt)} · trigger:{" "}
            {plan.generationTrigger}
          </Typography>
        )}
      </Stack>

      {loading ? (
        <Box sx={{ my: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : !plan ? (
        <Typography sx={{ my: 2 }} color="text.secondary">
          No active personalized plan for this org yet.
        </Typography>
      ) : (
        <Stack spacing={1.5} sx={{ maxWidth: 800 }}>
          {plan.steps.map((step) => (
            <Box
              key={step.id}
              sx={{
                border: "1px solid",
                borderColor: "rgba(1,67,57,0.1)",
                borderRadius: 2,
                bgcolor: "#fafaf6",
                p: 2,
                display: "flex",
                gap: 2,
                alignItems: "flex-start",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  minWidth: 20,
                  color: "text.secondary",
                }}
              >
                {step.priority}.
              </Typography>
              <Box sx={{ flex: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    flexWrap: "wrap",
                    mb: 0.5,
                  }}
                >
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {step.action}
                  </Typography>
                  <Chip label={step.type} size="small" variant="outlined" />
                  <Chip
                    label={step.status}
                    size="small"
                    sx={
                      step.status === "completed"
                        ? {
                            bgcolor: "#dcfce7",
                            border: "1px solid #7bf1a8",
                            color: "#008236",
                          }
                        : step.status === "archived"
                          ? {
                              bgcolor: "#f3f4f6",
                              border: "1px solid #d1d5dc",
                              color: "#364153",
                            }
                          : undefined
                    }
                  />
                  {step.targetTemplateName && (
                    <Chip
                      label={`template: ${step.targetTemplateName}`}
                      size="small"
                      variant="outlined"
                    />
                  )}
                  {step.targetBentoCategoryId != null && (
                    <Chip
                      label={`category: ${step.targetBentoCategoryId}`}
                      size="small"
                      variant="outlined"
                    />
                  )}
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {step.detail}
                </Typography>
              </Box>
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
}
